// End-of-turn resolution (PLAN §4). Deterministic, ordered, and run for the
// active player's network only — so "one square per turn" means one square per
// *my* turn, and both sides stay symmetric.
//
// A Gambit (the stage boundary's, `runGambit`) and the Full Production card
// call this same function repeatedly rather than reimplementing it: a bespoke fast path would be a second copy of
// the ordering rules below, and it would drift.

import { runWarehouses } from "./industry/warehouse";
import { type Color, type PieceType } from "./board";
import type { DisguiseBreak } from "./campaign/disguise";
import { advanceBelts, type CargoMove } from "./industry/belts";
import { runHealers } from "./industry/healers";
import { runPower } from "./industry/power";
import {
  grabFromAdjacent,
  acceptsUnit,
  transferBetweenPieces,
  runMinerHandoffs,
  runMining,
  runCommandedTicks,
  runMachineHandoffs,
  runRefineries
} from "./industry/machines";
import { createDemandCache } from "./industry/routing";
import {
  deliverUnit,
  type IndustryState,
  type StructureKind
} from "./industry/state";
import { runStores } from "./industry/structures";
import { runTrains } from "./industry/trains";
import { tallyHouseTicks } from "./industry/houseTicks";
import { livingSides, type Position } from "./position";
import { RULES } from "./rules";

/** One turret shot: who fired, at what, and whether the target fell. */
export interface TurretShot {
  from: number;
  to: number;
  killed: boolean;
}

export interface TickReport {
  mined: number;
  moved: number;
  /**
   * Every unit that changed places on a lane this tick.
   *
   * The renderer's substrate for animating ingots individually. A report like
   * everything else here — no rule reads it back and it is not in the hash —
   * and it has to come from the flow pass rather than be worked out from the
   * board afterwards, because a merge and an arrival leave the same board.
   */
  cargo?: CargoMove[];
  cooked: number;
  /**
   * Coal burned for fuel points this tick (§31 M2). Progress — a burn tick
   * cooks nothing and is still the factory working — so the Gambit counts it.
   */
  burned?: number;
  /** Of `cooked`, the metal units — a rung toward a troop. A report only, for the tuner. */
  cookedMetal?: number;
  /**
   * Health put back this tick — a Miracle Healer into the pieces around her
   * (`runHealers`).
   */
  healed?: number;
  /**
   * Squares trains ran this tick (§31 S7). Not progress: a train carries
   * nothing the economy counts, so a Gambit never waits on one.
   */
  trains?: number;
  /** Pieces turrets destroyed on this handoff. */
  killed?: number;
  /**
   * Every shot a turret took this handoff, in the order they were fired.
   *
   * **A count could not be drawn.** `killed` says something died and nothing
   * about where the guns were pointing — so a laser had nowhere to go and a
   * turret had nothing to turn towards. This is the same split `CargoMove`
   * makes: only the rules can say what happened, and only the renderer knows
   * where it was on screen.
   *
   * The order is the firing order, which the animation staggers by.
   */
  shots?: TurretShot[];
  minted: { square: number; type: PieceType; at: number }[];
  /**
   * Artifact structures stood up from tokens this turn (§26 F6).
   *
   * `minted`'s shape and `minted`'s reason: the structure is on the board the
   * frame the command lands, and the renderer draws the board — but which of
   * the structures it draws is *new* is a fact about the command, and a diff
   * would have to tell a placement from a level's camps arriving. Written by
   * `place-structure` after the tick, like the forge's report; no rule reads
   * it back and it is not in the hash.
   */
  placed?: { square: number; kind: StructureKind }[];
  /**
   * Costumes that came off this turn, and where the wearer ended up.
   *
   * A report like everything else here — no rule reads it back, it is not in
   * the hash, and the board after the move cannot reconstruct it: the disguise
   * is simply *gone*, which is exactly the state a diff cannot tell from "there
   * was never one". The same argument `CargoMove` makes about a merge and an
   * arrival leaving the same board.
   *
   * It exists because the alternative is a mechanic that ends silently: the
   * move that blows a cover is the moment the card is *about*, and without
   * this the only signal was a small model quietly no longer being drawn.
   */
  unmasked?: { square: number; reason: DisguiseBreak }[];
  /**
   * Material a sweep lifted off the ground, and where the sweeper landed.
   *
   * **Reported because the board cannot say it**, the standing argument for
   * everything else here: `vacuumAlong` takes scrap off a square, puts some of
   * it on a lane, hands some to a touching machine and sets the rest back down,
   * so the board after a king's move is one where several things changed and
   * nothing says which of them was the pickup. No rule reads it back and it is
   * not in the hash.
   *
   * `at` is the mover's landing square — a sweep runs along a path and finishes
   * where the piece is, which is the one square of it worth pointing at.
   */
  collected?: { at: number; amount: number };
}

/** True when this tick did real work — the Gambit's termination test. */
export const madeProgress = (report: TickReport): boolean =>
  report.mined > 0 || report.cooked > 0 || report.minted.length > 0 || (report.burned ?? 0) > 0;

export interface GambitReport extends TickReport {
  rounds: number;
  /** True when the fixpoint hit the round cap instead of settling. */
  capped: boolean;
}

/**
 * A Gambit (PLAN §3.6): every economy runs to exhaustion at once — a stage
 * boundary's and a level region's (single player). The two-player *offer* is
 * retired (§31 R4). Every heap with a miner drains, every unit in transit completes its
 * journey, every refinery cooks out, and every foundry mints until it runs out
 * of material — or out of empty squares to mint into.
 *
 * Two things to keep true here. It calls `runTick` repeatedly rather than
 * reimplementing it: a bespoke fast path would be a second copy of the
 * ordering rules above and would drift from the first. And it terminates on
 * *progress* — extraction, cooking or minting — not on state change, because a
 * ring of belt parts circulating cargo changes state forever without ever
 * getting anywhere. "Until resources are exhausted" is not literally reachable
 * either: an unattended heap has nothing to extract it and simply stays.
 *
 * Sides run in a fixed order, because two networked clients must agree.
 */
export function runGambit(
  position: Position,
  industry: IndustryState,
  /** Which economies to run. Defaults to every army still on the board. */
  sides: readonly Color[] = livingSides(position)
): GambitReport {
  const total: GambitReport = {
    mined: 0,
    moved: 0,
    cooked: 0,
    minted: [],
    rounds: 0,
    capped: false
  };

  while (total.rounds < RULES.gambitRoundCap) {
    let progress = false;

    // Each round is a turn's worth of work, so the clock that rate-limits
    // "once a turn" has to advance with it. Without this the Gambit digs one
    // unit per miner for the whole cash-out instead of draining every heap,
    // which is the opposite of running the economy to exhaustion.
    industry.turnCounter++;

    // Every army that still exists, not the two classic ones. From stage two
    // the enemy is side 2 or 3, so this ran an economy that was not on the
    // board and skipped the one that was.
    for (const owner of sides) {
      const report = runTick(position, industry, owner);
      total.mined += report.mined;
      total.moved += report.moved;
      total.cooked += report.cooked;
      if (report.burned) total.burned = (total.burned ?? 0) + report.burned;
      if (report.healed) total.healed = (total.healed ?? 0) + report.healed;
      total.minted.push(...report.minted);
      if (madeProgress(report)) progress = true;
    }

    total.rounds++;
    if (!progress) break;
  }

  total.capped = total.rounds >= RULES.gambitRoundCap;
  return total;
}

/**
 * Packages move. This is the *first* thing a turn does, before its owner has
 * touched anything.
 *
 * Splitting the tick in two is what makes the turn legible from the player's
 * seat: packages move -> you move and build -> machines run -> opponent. When
 * flow happened at the end of a turn along with everything else, a lane's
 * contents jumped at the same instant as the mint they fed, and there was no
 * moment where you could look at the board and see where your material had
 * got to.
 */
export function runFlow(
  position: Position,
  industry: IndustryState,
  owner: Color
): { moved: number; cargo: CargoMove[] } {
  // Warehouses hand out first, before anything else this tick accepts.
  //
  // That ordering *is* the rule that a store may never emit, in one tick, a
  // unit it accepted in that tick — without it a row of stores is a
  // teleporter, since the unit would arrive at the first and be passed along
  // the chain in the same pass. Going first needs no snapshot and no extra
  // state: everything a store lets go of here demonstrably predates this
  // tick's arrivals.
  runStores(position, industry, owner);
  // A Drone-Assisted Warehouse feeds hungry machines in reach (§33 I5).
  runWarehouses(position, industry, owner);

  // The callback commits as well as tests: a merged pile can hand a machine
  // several units in one tick, and a check against a buffer that is not being
  // updated as it goes would let it overflow.
  const flow = advanceBelts(position, industry, owner, (square, unit) => {
    // The *unit*, not just the square. This asked `machineAccepts`, which is a
    // question about room — so a lane running into a refinery handed it
    // whatever arrived, including things no refinery can ever process. Room is
    // necessary and has never been sufficient: routing a unit into a machine
    // that cannot use it strands it for the rest of the game.
    if (!acceptsUnit(position, industry, square, owner, unit)) return false;
    return deliverUnit(position, industry, square, unit);
  });

  return { moved: flow.moved, cargo: flow.moves };
}

/**
 * Machines run. The *second* half of a turn, after its owner has moved and
 * built, so that what you set up this turn is what the machines see.
 *
 * Mining and loading a belt run here too, and are *also* available as commands
 * the player can click during their turn. Both, deliberately: pressing the
 * button is the moment you get to watch it happen, but nobody should lose ore
 * because they forgot to press it. Clicking early just means the automatic
 * pass finds the work already done.
 */
export function runMachines(
  position: Position,
  industry: IndustryState,
  owner: Color,
  /** The last tick of `owner`'s turn: refiners burn no coal (§31 M2). */
  finalTick = false
): Omit<TickReport, "moved"> {
  // What each pass moved, tagged with which pass it was. Collected here rather
  // than returned separately by each: the three passes are one turn's worth of
  // machine work, and the renderer plays them back in this order.
  const cargo: CargoMove[] = [];

  // **One routing field per kind of material, for the whole pass.**
  //
  // Every hand-off asks the same question — where is this unit trying to get to
  // — and the answer depends only on the board's shape and the unit's grade and
  // provenance, neither of which changes while machines are running. Two bars
  // of iron of the same provenance are the same packed word, so the unit itself
  // is the key. Without this a factory of any size searches the network once
  // per ingot per pass, and the planner ticks a rollout hundreds of times a
  // turn.
  const routes = createDemandCache();

  // Three passes over *every* machine, rather than each machine doing its
  // whole job before the next one starts: everything accepts, then everything
  // processes, then everything hands on.
  //
  // That ordering is what lets a refinery standing between two lanes run a
  // continuous stream. It takes a unit off the inbound lane, cooks what was
  // already in the oven, and puts the finished unit on the outbound lane — all
  // in the same turn — instead of the three steps being spread across three
  // turns depending on which machine the sweep reached first.
  //
  // The one-turn cook survives, and it is worth being explicit about why: the
  // process pass empties the oven *before* it reloads it, so a unit accepted
  // this turn goes into an oven that has just been vacated and can only come
  // out next turn. Reversing those two lines inside `runRefineries` would let
  // material arrive and leave refined on the same tick.

  // 1. Accept. Harvesters dig, and consumers pull from any adjacent part of
  //    theirs — not just one aimed into them, so a lane running past a bishop
  //    feeds it.
  const mined = runMining(position, industry, owner);
  grabFromAdjacent(position, industry, owner, cargo);

  // 2. Process. Refineries move the oven on a grade. Foundries do not mint
  //    here any more — forging costs a turn and is something the player does,
  //    so `minted` only ever reports what a `forge` command produced.

  // The healers, immediately before the ovens they are choosing against:
  // mending or cooking, never both, so the two passes are one decision. `busy`
  // is the healers that found work.
  const { healed, busy } = runHealers(position, owner);
  // **The power network** (§31 S6): generators and batteries fill the pools
  // of the ovens that want a point, before the ovens would burn coal.
  runPower(position, industry, owner);
  const burned = { count: 0 };
  const metal = { count: 0 };
  const cooked = runRefineries(position, industry, owner, cargo, busy, finalTick, burned, metal);
  const minted: TickReport["minted"] = [];

  // 3. Hand off. Miners put ore down, machines push their output toward
  //    whatever will take it, and carriers walk a unit one step onward.
  runMinerHandoffs(position, industry, owner, cargo, routes);
  // **A Marshal's neighbours take a second miner's turn, here and not in the
  // accept pass.** A pawn's buffer is two and its rate is two, so a second dig
  // before the delivery is refused for `buffer-full` and buys nothing; after
  // it, the ore has somewhere to go. See `runCommandedTicks`.
  const commanded = runCommandedTicks(position, industry, owner, cargo, routes);
  runMachineHandoffs(position, industry, owner, cargo, routes);
  transferBetweenPieces(position, industry, owner, cargo, routes);

  // 4. Trains (§31 S7): engines with an order run, after every hand-off, so
  //    the coal a station delivered this tick is in the tank.
  const trains = runTrains(position, industry, owner);
  // A House of Cards counts this tick of its side's (§31 A11).
  tallyHouseTicks(industry, owner);

  // (No build pass: a belt part is paid when it is laid, §31 S2.)

  // The bonus dig counts as mining, or a Marshal doubles the ore and the
  // report says nothing happened — and `progressed` reads this, which is what
  // decides whether a Gambit has run out of things to do.
  //
  // `healed` is deliberately not in that test, which is what makes a healer
  // safe in a Gambit: a round that only mended is no progress and the fixpoint
  // ends. Mending saturates anyway.
  return {
    mined: mined + commanded,
    cooked,
    minted,
    cargo,
    healed,
    ...(burned.count > 0 ? { burned: burned.count } : {}),
    ...(metal.count > 0 ? { cookedMetal: metal.count } : {}),
    ...(trains > 0 ? { trains } : {})
  };
}

/**
 * Add one tick's report to a running total.
 *
 * **One body, because a turn can hold several ticks and the folding had begun
 * to drift.** `runGambit` accumulates four fields and `endTurn`'s own per-side
 * loop five, so a field added to `TickReport` has to reach each of them, and a
 * miss is silent because nothing asserts on a report.
 *
 * It folds **everything `runTick` can produce**, which is not everything a
 * *turn* can produce: that function drops the flow pass's `cargo` and
 * never runs turrets, so `killed` and `shots` cannot appear here. `healed` left that list when the Miracle Healer shipped — hers is
 * a machine pass, so a bought tick heals and this carries it. The two older folds are deliberately
 * left alone — `runGambit` reports its own shape, and merging the three is the
 * `runSideTick` unit §26 F4 names rather than a line in this one.
 *
 * Pushed one at a time rather than spread: a dozen ticks of a busy factory is a
 * long cargo list, and `push(...long)` is an argument list rather than a loop.
 */
export function foldTick(into: TickReport, from: TickReport): void {
  into.mined += from.mined;
  into.moved += from.moved;
  into.cooked += from.cooked;
  if (from.burned) into.burned = (into.burned ?? 0) + from.burned;
  if (from.cookedMetal) into.cookedMetal = (into.cookedMetal ?? 0) + from.cookedMetal;
  // Left absent on a board with no healer, rather than a zero every report
  // carries forever.
  if (from.healed) into.healed = (into.healed ?? 0) + from.healed;
  for (const one of from.minted) into.minted.push(one);
  if (from.cargo?.length) {
    into.cargo ??= [];
    for (const one of from.cargo) into.cargo.push(one);
  }
}

/**
 * `count` extra whole ticks of one side's factory, back to back.
 *
 * What "+1 tick a turn" is made of (§26 F4), and what the two cards that buy
 * ticks spend. Two things it has to get right, both already settled
 * elsewhere in this file:
 *
 * - **The turn counter advances with each one**, exactly as `runGambit` does
 *   and for its reason: everything rate-limited to once a turn compares
 *   against `industry.turnCounter`, so an extra tick that left it alone would
 *   move belts and cook what was already in an oven and **mine nothing** —
 *   which is most of an economy, and would make the grant read as broken.
 * - **One side's**, never the board's. Every factory ticks on every move
 *   already; this is the extra one somebody paid for.
 */
export function runExtraTicks(
  position: Position,
  industry: IndustryState,
  owner: Color,
  count: number
): TickReport {
  const total: TickReport = { mined: 0, moved: 0, cooked: 0, minted: [], cargo: [] };
  // **After each tick, never before** (§31 T3): `runSideTick`'s convention,
  // so the counter is always fresh when the next tick starts — a bought tick
  // followed by End Turn's would otherwise share a count and dig nothing.
  for (let i = 0; i < count; i++) {
    foldTick(total, runTick(position, industry, owner));
    industry.turnCounter++;
  }
  return total;
}

/** Both halves back to back — the Gambit's fixpoint wants a whole tick. */
export function runTick(
  position: Position,
  industry: IndustryState,
  owner: Color
): TickReport {
  const { moved } = runFlow(position, industry, owner);
  return { moved, ...runMachines(position, industry, owner) };
}
